import { CaretDown, CheckCircle, Link as LinkIcon, Star } from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext } from 'react-router-dom'
import type { DashboardOutletContext } from '../components/AppShell'
import { Button, ConflictDialog, Drawer, Field, StatusBadge } from '../components/common'
import { HistoryPanel } from '../components/HistoryPanel'
import { WorkingItemEditorDrawer } from '../components/WorkingItemEditorDrawer'
import { ProjectFormView, projectFormLabels, type ProjectForm } from './ProjectsPage'
import { api, conflictCurrent } from '../lib/api'
import { defaultReadinessStatusColor, formatUpdatedAt, readinessStatusColors, readinessStatuses, workingItemOwners, workingStatusLabel, workingStatusLight } from '../lib/constants'
import { compressImageToWebp, IMAGE_LIMITS, type CompressedImage } from '../lib/image-compression'
import { sortOrderBefore } from '../lib/sort'
import type { DashboardData, Project, ReadinessItem, WorkingItem } from '../lib/types'

type ReadinessForm = Pick<ReadinessItem, 'projectName' | 'groupName' | 'item' | 'triggerText' | 'status' | 'statusColor' | 'duration' | 'startDate' | 'endDate' | 'testers'>

const readinessLabels = { projectName: 'Project', groupName: '四大類別', item: '測項名稱', triggerText: 'Trigger / Release', status: '狀態', statusColor: '顯示顏色', duration: '測試預估時間', startDate: '開始時間', endDate: '結束時間', testers: '測試人員' }

interface PendingAttachment extends CompressedImage { id: string; previewUrl: string }
const projectStageLabels = ['未啟動', '專案啟動與前期規劃', '物料申請與測試準備', '測試啟動與執行', '完成'] as const

export const ReadinessProjectFormView = ProjectFormView

const groupOrder = ['ESS', 'FW', 'FW Team Work', 'Automatic'] as const
export const activeReadinessStatuses = new Set<ReadinessItem['status']>(['準備測試', '待測試', '測試中', '警示', '阻塞'])
const groupShortLabels: Record<string, string> = { ESS: 'ESS', FW: 'FW', 'FW Team Work': 'TEAM', Automatic: 'AUTO' }
const groupMeta: Record<string, { description: string; defaultDuration: string }> = {
  ESS: { description: '核心 ESS 測試', defaultDuration: '約 2–3 週' },
  FW: { description: '生產前 FW Stability Validation', defaultDuration: '約半天' },
  'FW Team Work': { description: '依 FW Team / System Leader 需求支援', defaultDuration: 'VCC 約 35 分鐘' },
  Automatic: { description: '配合 Dell 建立自動化測試', defaultDuration: '依 Dell 當週分配' },
}
const emptyReadinessForm = (projectName = ''): ReadinessForm => ({ projectName, groupName: 'ESS', item: '', triggerText: '', status: '待確認', statusColor: 'white', duration: '', startDate: '', endDate: '', testers: [] })

export function selectActiveReadinessProjects(projects: Project[], readiness: ReadinessItem[], query = '', selectedTester: string | null = null) {
  const normalizedQuery = query.trim().toLowerCase()
  return projects.filter((row) => row.focus).map((priorityProject) => ({
    project: priorityProject,
    rows: readiness.filter((row) =>
      row.projectName === priorityProject.name
      && activeReadinessStatuses.has(row.status)
      && (!selectedTester || row.testers.includes(selectedTester))
      && `${row.item} ${row.groupName} ${row.triggerText} ${row.testers.join(' ')}`.toLowerCase().includes(normalizedQuery)
    ),
  })).filter((entry) => entry.rows.length > 0)
}


export function ReadinessPage() {
  const { data, refresh, canEdit } = useOutletContext<DashboardOutletContext>()
  const queryClient = useQueryClient()
  const focusProjects = useMemo(() => {
    const focused = data.projects.filter((row) => row.focus)
    return focused.length ? focused : data.projects
  }, [data.projects])
  const priorityProjects = useMemo(() => data.projects.filter((row) => row.focus), [data.projects])
  const projectNames = useMemo(() => [...new Set([...data.projects.map((row) => row.name), ...data.readiness.map((row) => row.projectName)])], [data.projects, data.readiness])
  const workingItemsById = useMemo(() => new Map(data.workingItems.map((item) => [item.id, item])), [data.workingItems])
  const workingItemNumbers = useMemo(() => new Map(data.workingItems.map((item, index) => [item.id, `#${String(index + 1).padStart(2, '0')}`])), [data.workingItems])
  const [project, setProject] = useState(focusProjects[0]?.name ?? projectNames[0] ?? '')
  const [query, setQuery] = useState('')
  const [selectedTester, setSelectedTester] = useState<string | null>(null)
  const [editing, setEditing] = useState<ReadinessItem | null>(null)
  const [form, setForm] = useState<ReadinessForm>(() => emptyReadinessForm(focusProjects[0]?.name ?? projectNames[0]))
  const [open, setOpen] = useState(false)
  const [conflict, setConflict] = useState<ReadinessItem | null>(null)
  const [message, setMessage] = useState('')
  const [workingEditing, setWorkingEditing] = useState<WorkingItem | null>(null)
  const [viewMode, setViewMode] = useState<'project' | 'active'>('active')

  // Project edit drawer state
  const [projectDrawerOpen, setProjectDrawerOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [projectConflict, setProjectConflict] = useState<Project | null>(null)
  const [projectForm, setProjectForm] = useState<ProjectForm>({ type: 'PowerEdge', name: '', groupName: '', focus: false, stage: 0, action: '', description: '', status: '待啟動', statusColor: 'gray', targetDate: '', dateNote: '', subtasks: [], links: [], notes: '' })
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const [attachmentBusy, setAttachmentBusy] = useState(false)
  const [attachmentMessage, setAttachmentMessage] = useState('')

  const [draggingProjectId, setDraggingProjectId] = useState<string | null>(null)
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null)

  const projectAttachmentsQuery = useQuery({
    queryKey: ['project-attachments', editingProject?.id],
    queryFn: () => api.projectAttachments(editingProject!.id),
    enabled: !!editingProject?.id,
  })

  useEffect(() => {
    if (!project && focusProjects[0]) setProject(focusProjects[0].name)
  }, [focusProjects, project])

  useEffect(() => {
    if (!message) return
    const timer = setTimeout(() => setMessage(''), 2500)
    return () => clearTimeout(timer)
  }, [message])

  const selectedProject = data.projects.find((row) => row.name === project)
  const rows = useMemo(() => data.readiness.filter((row) =>
    row.projectName === project
    && (!selectedTester || row.testers.includes(selectedTester))
    && `${row.item} ${row.groupName} ${row.triggerText} ${row.testers.join(' ')}`.toLowerCase().includes(query.toLowerCase())
  ), [data.readiness, project, query, selectedTester])
  const visibleGroups = useMemo(() => {
    const extras = rows.map((row) => row.groupName).filter((name) => name && !groupOrder.includes(name as typeof groupOrder[number]))
    return [...groupOrder, ...new Set(extras)]
  }, [rows])
  const activeProjectGroups = useMemo(() => {
    return selectActiveReadinessProjects(priorityProjects, data.readiness, query, selectedTester)
  }, [data.readiness, priorityProjects, query, selectedTester])
  const activeItemCount = useMemo(() => activeProjectGroups.reduce((total, entry) => total + entry.rows.length, 0), [activeProjectGroups])
  const allActiveItemCount = useMemo(() => selectActiveReadinessProjects(priorityProjects, data.readiness).reduce((total, entry) => total + entry.rows.length, 0), [data.readiness, priorityProjects])

  const save = useMutation({
    mutationFn: () => editing ? api.updateEntity<ReadinessItem>('readiness', editing.id, editing.version, form) : api.createEntity<ReadinessItem>('readiness', form),
    onSuccess: async () => { close(); setMessage('測試準備資料已儲存。'); await refresh() },
    onError: (error) => { const latest = conflictCurrent<ReadinessItem>(error); latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '儲存失敗。') }
  })
  const close = () => { setOpen(false); setEditing(null); setConflict(null) }
  const edit = (row: ReadinessItem) => { setEditing(row); setForm(toReadinessForm(row)); setOpen(true); setConflict(null) }
  const chooseProject = (name: string) => { setViewMode('project'); setProject(name); setSelectedTester(null); setQuery('') }

  const queueAttachments = async (files: File[]) => {
    const existingCount = (projectAttachmentsQuery.data?.length ?? 0) - removedAttachmentIds.length
    const available = IMAGE_LIMITS.maxAttachments - existingCount - pendingAttachments.length
    if (available <= 0) { setAttachmentMessage(`每個 Project 最多可保存 ${IMAGE_LIMITS.maxAttachments} 張圖片。`); return }
    setAttachmentBusy(true); setAttachmentMessage('')
    const queued: PendingAttachment[] = []
    const errors: string[] = []
    for (const file of files.slice(0, available)) {
      try { const c = await compressImageToWebp(file); queued.push({ ...c, id: crypto.randomUUID(), previewUrl: URL.createObjectURL(c.file) }) }
      catch (error) { errors.push(error instanceof Error ? error.message : '圖片壓縮失敗。') }
    }
    setPendingAttachments((current) => [...current, ...queued])
    if (files.length > available) errors.push(`已達 ${IMAGE_LIMITS.maxAttachments} 張上限，其餘圖片未加入。`)
    setAttachmentMessage(errors[0] ?? (queued.length ? `已將 ${queued.length} 張圖片轉成 WebP，儲存時會上傳。` : ''))
    setAttachmentBusy(false)
  }

  const saveProject = useMutation({
    mutationFn: async () => {
      if (!editingProject) return
      const saved = await api.updateEntity<Project>('projects', editingProject.id, editingProject.version, projectForm)
      const failedPendingIds: string[] = []
      const failedDeleteIds: string[] = []
      let uploaded = 0; let deleted = 0
      for (const id of removedAttachmentIds) {
        try { await api.deleteProjectAttachment(saved.id, id); deleted += 1 } catch { failedDeleteIds.push(id) }
      }
      for (const pending of pendingAttachments) {
        try { await api.uploadProjectAttachment(saved.id, pending); uploaded += 1 } catch { failedPendingIds.push(pending.id) }
      }
      const startCount = projectAttachmentsQuery.data?.length ?? editingProject.attachmentCount ?? 0
      return { saved: { ...saved, attachmentCount: Math.max(0, startCount - deleted + uploaded) }, failedPendingIds, failedDeleteIds }
    },
    onSuccess: (result) => {
      if (!result) return
      const { saved, failedPendingIds, failedDeleteIds } = result
      void queryClient.invalidateQueries({ queryKey: ['project-attachments', saved.id] })
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) =>
        old ? { ...old, projects: old.projects.map((p) => p.id === saved.id ? saved : p) } : old
      )
      if (failedPendingIds.length || failedDeleteIds.length) {
        setEditingProject(saved); setProjectForm(toProjectForm(saved))
        setPendingAttachments((c) => c.filter((item) => { if (failedPendingIds.includes(item.id)) return true; URL.revokeObjectURL(item.previewUrl); return false }))
        setRemovedAttachmentIds(failedDeleteIds)
        setAttachmentMessage(`專案已儲存，但有 ${failedPendingIds.length + failedDeleteIds.length} 張圖片處理失敗，請再儲存一次重試。`)
        return
      }
      closeProjectDrawer()
      setMessage(`「${saved.name}」Project 已儲存。`)
    },
    onError: (error) => {
      const latest = conflictCurrent<Project>(error)
      if (latest) setProjectConflict(latest)
      else setMessage(error instanceof Error ? error.message : '儲存失敗。')
    }
  })

  const openProjectDrawer = (p: Project) => {
    setPendingAttachments([]); setRemovedAttachmentIds([]); setAttachmentMessage('')
    setEditingProject(p); setProjectForm(toProjectForm(p)); setProjectConflict(null); setProjectDrawerOpen(true)
  }
  const closeProjectDrawer = () => {
    setPendingAttachments((c) => { c.forEach((item) => URL.revokeObjectURL(item.previewUrl)); return [] })
    setRemovedAttachmentIds([]); setAttachmentMessage(''); setAttachmentBusy(false)
    setProjectDrawerOpen(false); setEditingProject(null); setProjectConflict(null)
  }


  const dropOnProject = async (target: Project) => {
    const draggedId = draggingProjectId
    setDraggingProjectId(null)
    setDragOverProjectId(null)
    if (!draggedId || draggedId === target.id) return

    const dragged = data.projects.find((row) => row.id === draggedId)
    if (!dragged) return

    const without = focusProjects.filter((row) => row.id !== dragged.id)
    const targetIndex = without.findIndex((row) => row.id === target.id)
    if (targetIndex < 0) return
    const sortOrder = sortOrderBefore(without, targetIndex)

    const reordered = [...data.projects]
    const fromIdx = reordered.findIndex((item) => item.id === dragged.id)
    if (fromIdx < 0) return
    const [moved] = reordered.splice(fromIdx, 1)
    const toIdx = reordered.findIndex((item) => item.id === target.id)
    reordered.splice(toIdx, 0, { ...moved, sortOrder })

    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return { ...old, projects: reordered }
    })

    try {
      const updated = await api.updateEntity<Project>('projects', dragged.id, dragged.version, { sortOrder })
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          projects: old.projects.map((p) => (p.id === updated.id ? { ...p, sortOrder: updated.sortOrder, version: updated.version } : p)),
        }
      })
      setMessage(`重點專案排序已更新。`)
    } catch (error) {
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return { ...old, projects: data.projects }
      })
      setMessage(error instanceof Error ? error.message : '專案排序失敗。')
    }
  }

  return <section className="page-stack readiness-page">
    <div className="working-header-bar">
      <div className="working-header-title-group"><h1>測試準備與時程 (Project Readiness)</h1></div>
      <div className="readiness-view-toggle" role="group" aria-label="Readiness 檢視模式">
        <button type="button" className={viewMode === 'project' ? 'active' : ''} onClick={() => { setViewMode('project'); setQuery(''); setSelectedTester(null) }}>單一專案</button>
        <button type="button" className={viewMode === 'active' ? 'active' : ''} onClick={() => { setViewMode('active'); setQuery(''); setSelectedTester(null) }}>進行項目總覽 <span>{allActiveItemCount}</span></button>
      </div>
    </div>

    {message ? <div className="floating-toast"><span>{message}</span><button type="button" onClick={() => setMessage('')} aria-label="關閉通知">×</button></div> : null}

    <div className="readiness-project-switcher">
      <strong>重點專案：</strong>
      <div className="readiness-project-buttons">
        {focusProjects.map((row) => (
          <button
            key={row.id}
            type="button"
            draggable={canEdit}
            onDragStart={(e) => {
              if (!canEdit) return
              setDraggingProjectId(row.id)
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', row.id)
            }}
            onDragEnd={() => {
              setDraggingProjectId(null)
              setDragOverProjectId(null)
            }}
            onDragOver={(e) => {
              if (!canEdit || !draggingProjectId) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
            }}
            onDragEnter={(e) => {
              if (!canEdit || !draggingProjectId || draggingProjectId === row.id) return
              e.preventDefault()
              setDragOverProjectId(row.id)
            }}
            onDragLeave={(e) => {
              if (dragOverProjectId === row.id && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setDragOverProjectId(null)
              }
            }}
            onDrop={(e) => {
              if (!canEdit) return
              e.preventDefault()
              void dropOnProject(row)
            }}
            className={`${viewMode === 'project' && project === row.name ? 'active' : ''} ${draggingProjectId === row.id ? 'dragging' : ''} ${dragOverProjectId === row.id ? 'drag-over' : ''}`}
            onClick={() => {
              if (draggingProjectId) return
              chooseProject(row.name)
            }}
            title={canEdit ? '點擊切換專案，或按住拖曳交換位置' : undefined}
          >
            <Star size={13} weight="fill" /><span>{projectDisplayName(row)}</span><small>({row.type})</small>
          </button>
        ))}
      </div>
    </div>

    {viewMode === 'project' ? <>
    <div className="readiness-summary-strip">
      <SummaryCell label="目前專案" value={selectedProject ? projectDisplayName(selectedProject) : (project || '—')} detail={selectedProject?.type} />
      <SummaryCell label="目前階段" value={selectedProject ? stageLabel(selectedProject.stage) : '—'} />
      <div className="readiness-summary-cell"><span>專案狀態</span><div>{selectedProject ? <StatusBadge status={selectedProject.status} tone={selectedProject.statusColor} /> : '—'}</div></div>
      <SummaryCell label="預計測試" value={selectedProject?.targetDate || 'TBD'} />
      <div className="readiness-summary-search">
        <input className="search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜尋測項、Trigger 或 Tester" />
        {selectedTester ? <div className="owner-filter-chip"><span>Tester: <strong>{selectedTester}</strong></span><button type="button" onClick={() => setSelectedTester(null)} aria-label="清除 Tester 篩選">×</button></div> : null}
      </div>
    </div>

    <ProjectDescription key={selectedProject?.id ?? project} selectedProject={selectedProject ?? null} canEdit={canEdit} onEdit={openProjectDrawer} />

    <div className="readiness-card-grid">
      {visibleGroups.map((group) => {
        const groupRows = rows.filter((row) => row.groupName === group)
        const meta = groupMeta[group] ?? { description: '自訂測試類別', defaultDuration: '依測項設定' }
        return (
          <section key={group} className="readiness-card">
            <header>
              <div><strong>{group}</strong><small>{meta.description}</small></div>
              <span className="readiness-duration">{groupDuration(groupRows, meta.defaultDuration)}</span>
            </header>
            <ReadinessTable
              rows={groupRows}
              canEdit={canEdit}
              selectedTester={selectedTester}
              workingItemsById={workingItemsById}
              workingItemNumbers={workingItemNumbers}
              onEdit={edit}
              onSelectTester={(tester) => setSelectedTester((current) => current === tester ? null : tester)}
              onOpenWorkingItem={setWorkingEditing}
            />
            <footer><CheckCircle size={15} weight="duotone" /> {groupProgressLabel(groupRows)}</footer>
          </section>
        )
      })}
    </div>
    </> : <>
      <div className="readiness-overview-toolbar">
        <div><strong>進行項目總覽</strong><span>{activeProjectGroups.length} 個重點專案 · {activeItemCount} 個進行項目</span></div>
        <div className="readiness-summary-search">
          <input className="search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜尋測項、Trigger 或 Tester" />
          {selectedTester ? <div className="owner-filter-chip"><span>Tester: <strong>{selectedTester}</strong></span><button type="button" onClick={() => setSelectedTester(null)} aria-label="清除 Tester 篩選">×</button></div> : null}
        </div>
      </div>

      <div className="readiness-overview-grid">
        {activeProjectGroups.map(({ project: priorityProject, rows: activeRows }) => (
          <section key={priorityProject.id} className="readiness-card readiness-overview-card">
            <header className="readiness-overview-card-header">
              <div className="readiness-overview-project-title">
                <strong>{projectDisplayName(priorityProject)}</strong>
                <small>{priorityProject.type}</small>
              </div>
              <span className="readiness-active-count">{activeRows.length} 項進行中</span>
            </header>
            <div className="readiness-overview-project-meta">
              <span><small>目前階段</small><strong>{stageLabel(priorityProject.stage)}</strong></span>
              <span><small>專案狀態</small><StatusBadge status={priorityProject.status} tone={priorityProject.statusColor} /></span>
              <span><small>預計測試</small><strong>{priorityProject.targetDate || 'TBD'}</strong></span>
            </div>
            <button type="button" className="readiness-overview-description" onClick={() => canEdit && openProjectDrawer(priorityProject)} disabled={!canEdit} title={canEdit ? '點擊編輯專案現況' : priorityProject.description || '尚未填寫專案現況'}>
              <strong>專案現況</strong>
              <span>{priorityProject.description || '尚未填寫專案現況。'}</span>
              {canEdit ? <small>點擊編輯</small> : null}
            </button>
            <ReadinessTable
              rows={activeRows}
              canEdit={canEdit}
              selectedTester={selectedTester}
              workingItemsById={workingItemsById}
              workingItemNumbers={workingItemNumbers}
              showGroup
              onEdit={edit}
              onSelectTester={(tester) => setSelectedTester((current) => current === tester ? null : tester)}
              onOpenWorkingItem={setWorkingEditing}
            />
            <footer><CheckCircle size={15} weight="duotone" />進行項目 {activeRows.length}</footer>
          </section>
        ))}
        {activeProjectGroups.length === 0 ? <div className="readiness-overview-empty">目前沒有符合條件的進行項目。</div> : null}
      </div>
    </>}

    <Drawer open={open} title={editing ? `${editing.projectName} · ${editing.item}` : '新增測試項目'} subtitle={editing ? `最後更新: ${formatUpdatedAt(editing.updatedAt)} 由 ${editing.updatedByName}` : project} onClose={close} footer={<><Button onClick={close}>取消</Button><Button variant="primary" type="submit" form="readiness-form" disabled={save.isPending}>{save.isPending ? '儲存中…' : '儲存'}</Button></>}>
      <ReadinessFormView form={form} setForm={setForm} projectNames={projectNames} onSubmit={(event) => { event.preventDefault(); save.mutate() }} />
      <HistoryPanel entity="readiness" id={editing?.id} />
    </Drawer>

    <Drawer
      open={projectDrawerOpen}
      title={editingProject ? `編輯 Project · ${editingProject.name}` : ''}
      subtitle={editingProject ? `版本 ${editingProject.version} · ${editingProject.updatedByName} · ${formatUpdatedAt(editingProject.updatedAt)}` : ''}
      onClose={closeProjectDrawer}
      footer={<><Button onClick={closeProjectDrawer}>取消</Button><Button variant="primary" form="project-desc-form" type="submit" disabled={saveProject.isPending}>{saveProject.isPending ? '儲存中…' : '儲存'}</Button></>}
    >
      <ReadinessProjectFormView
        formId="project-desc-form"
        projectId={editingProject?.id}
        form={projectForm}
        setForm={setProjectForm}
        existingAttachments={projectAttachmentsQuery.data}
        pendingAttachments={pendingAttachments}
        removedAttachmentIds={removedAttachmentIds}
        attachmentLoading={projectAttachmentsQuery.isLoading}
        attachmentBusy={attachmentBusy}
        attachmentMessage={attachmentMessage}
        onQueueAttachments={queueAttachments}
        onRemoveExistingAttachment={(id) => setRemovedAttachmentIds((prev) => [...prev, id])}
        onRemovePendingAttachment={(id) => {
          setPendingAttachments((c) => c.filter((item) => { if (item.id !== id) return true; URL.revokeObjectURL(item.previewUrl); return false }))
        }}
        onSubmit={(e) => { e.preventDefault(); saveProject.mutate() }}
      />
      <HistoryPanel entity="project" id={editingProject?.id} />
    </Drawer>
    <ConflictDialog
      open={Boolean(projectConflict)}
      local={projectForm as unknown as Record<string, unknown>}
      remote={(projectConflict ?? {}) as unknown as Record<string, unknown>}
      labels={projectFormLabels}
      onClose={() => setProjectConflict(null)}
      onUseLatest={() => {
        if (!projectConflict) return
        setEditingProject(projectConflict)
        setProjectForm(toProjectForm(projectConflict))
        setProjectConflict(null)
      }}
    />

    <WorkingItemEditorDrawer
      open={Boolean(workingEditing)}
      item={workingEditing}
      data={data}
      onClose={() => setWorkingEditing(null)}
      onMessage={setMessage}
    />

    <ConflictDialog open={Boolean(conflict)} local={form as unknown as Record<string, unknown>} remote={(conflict ?? {}) as unknown as Record<string, unknown>} labels={readinessLabels} onClose={() => setConflict(null)} onUseLatest={() => { if (conflict) { setEditing(conflict); setForm(toReadinessForm(conflict)); setConflict(null) } }} />
  </section>
}

export function ReadinessWorkingLinks({
  items,
  itemNumbers,
  onOpen,
}: {
  items: WorkingItem[]
  itemNumbers: Map<string, string>
  onOpen: (item: WorkingItem) => void
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 340, above: false })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(360, window.innerWidth - 24)
      const estimatedHeight = Math.min(280, 18 + items.length * 56)
      const above = window.innerHeight - rect.bottom < Math.min(180, estimatedHeight) && rect.top > estimatedHeight
      setPosition({
        width,
        left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
        top: above ? Math.max(12, rect.top - estimatedHeight - 6) : rect.bottom + 6,
        above,
      })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [items.length, open])

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!buttonRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', closeOutside)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  if (!items.length) return null

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`readiness-working-link-trigger ${open ? 'active' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${items.length} 個相關 Working Item`}
        title={`${items.length} 個相關 Working Item`}
        onClick={(event) => { event.stopPropagation(); setOpen((value) => !value) }}
      >
        <LinkIcon size={12} weight="bold" />
        <span>{items.length}</span>
        <CaretDown size={10} weight="bold" className={open ? 'open' : ''} />
      </button>
      {open ? createPortal(
        <div
          ref={popoverRef}
          className={`readiness-working-popover ${position.above ? 'above' : ''}`}
          style={{ top: position.top, left: position.left, width: position.width }}
          role="menu"
          aria-label="相關 Working Item"
          onClick={(event) => event.stopPropagation()}
        >
          {items.map((item) => {
            const number = itemNumbers.get(item.id) ?? '#—'
            return (
              <button key={item.id} type="button" role="menuitem" onClick={() => { setOpen(false); onOpen(item) }}>
                <span className="readiness-working-number">{number}</span>
                <span className="readiness-working-popover-main"><strong>{item.task}</strong><small>{item.owner || '—'}</small></span>
                <span className={`working-status-light ${workingStatusLight(item.status)}`} aria-label={workingStatusLabel(item.status)} title={workingStatusLabel(item.status)} />
              </button>
            )
          })}
        </div>,
        document.body,
      ) : null}
    </>
  )
}

function SummaryCell({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return <div className="readiness-summary-cell"><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div>
}

interface ReadinessTableProps {
  rows: ReadinessItem[]
  canEdit: boolean
  selectedTester: string | null
  workingItemsById: Map<string, WorkingItem>
  workingItemNumbers: Map<string, string>
  showGroup?: boolean
  onEdit: (row: ReadinessItem) => void
  onSelectTester: (tester: string) => void
  onOpenWorkingItem: (item: WorkingItem) => void
}

function ReadinessTable({ rows, canEdit, selectedTester, workingItemsById, workingItemNumbers, showGroup = false, onEdit, onSelectTester, onOpenWorkingItem }: ReadinessTableProps) {
  return <div className="table-wrap readiness-mini-table">
    <table>
      <thead><tr><th>Test Item</th><th>Trigger / Release</th><th>測試預估時間</th><th>測試時間</th><th className="working-col-owner">測試人員</th><th>狀態</th></tr></thead>
      <tbody>
        {rows.map((row) => {
          const notRequired = row.status === '不需測試'
          return <tr key={row.id} className={`clickable-row ${notRequired ? 'readiness-not-required' : ''}`} onClick={() => canEdit && onEdit(row)}>
            <td>
              <div className="readiness-item-title">
                {showGroup ? <span className="readiness-group-badge" title={row.groupName}>{groupShortLabels[row.groupName] ?? row.groupName}</span> : null}
                <strong>{row.item}</strong>
                <ReadinessWorkingLinks
                  items={(row.workingItemIds ?? []).map((id) => workingItemsById.get(id)).filter((item): item is WorkingItem => Boolean(item))}
                  itemNumbers={workingItemNumbers}
                  onOpen={onOpenWorkingItem}
                />
              </div>
            </td>
            <td title={row.triggerText || undefined}>{row.triggerText || '—'}</td>
            <td>{notRequired ? <span className="muted-dash">—</span> : <span className="readiness-duration-badge">{row.duration || '—'}</span>}</td>
            <td className="readiness-time-cell">{notRequired ? '—' : timeRange(row)}</td>
            <td className="working-owner-column">
              {!notRequired && row.testers.length ? <div className="working-owner-tags">{row.testers.map((tester) => {
                const isSelected = selectedTester === tester
                return <button key={tester} type="button" className={`working-owner-chip ${isSelected ? 'active' : ''}`} onClick={(event) => { event.stopPropagation(); onSelectTester(tester) }} title={isSelected ? `取消 Tester 篩選：${tester}` : `篩選 Tester：${tester}`}>{tester}</button>
              })}</div> : <span className="muted-dash">—</span>}
            </td>
            <td><StatusBadge status={row.status} tone={row.statusColor} /></td>
          </tr>
        })}
        {rows.length === 0 ? <tr><td colSpan={6} className="table-empty">目前沒有符合條件的測試項目。</td></tr> : null}
      </tbody>
    </table>
  </div>
}

function ProjectDescription({ selectedProject, canEdit, onEdit }: { selectedProject: Project | null; canEdit: boolean; onEdit: (p: Project) => void }) {
  const description = selectedProject?.description ?? ''
  const textRef = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  useLayoutEffect(() => {
    const element = textRef.current
    if (!element || expanded) return
    const measure = () => setCanExpand(element.scrollHeight > element.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [description, expanded])

  return (
    <section
      className={`readiness-description-panel ${canEdit && selectedProject ? 'clickable' : ''}`}
      onClick={() => {
        if (canEdit && selectedProject) onEdit(selectedProject)
      }}
      title={canEdit && selectedProject ? '點擊編輯專案現況' : undefined}
      role={canEdit && selectedProject ? 'button' : undefined}
      tabIndex={canEdit && selectedProject ? 0 : undefined}
      onKeyDown={(e) => {
        if (canEdit && selectedProject && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onEdit(selectedProject)
        }
      }}
    >
      <div className="readiness-description-header">
        <strong>專案現況</strong>
        {canEdit && selectedProject ? <span className="readiness-description-hint">點擊編輯</span> : null}
      </div>
      <p ref={textRef} className={`${expanded ? 'expanded ' : ''}${description ? '' : 'empty'}`}>
        {description || '尚未填寫專案現況。'}
      </p>
      {canExpand ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((value) => !value)
          }}
        >
          {expanded ? '收合' : '展開完整內容'}
        </button>
      ) : null}
    </section>
  )
}

function ReadinessFormView({ form, setForm, projectNames, onSubmit }: { form: ReadinessForm; setForm: (value: ReadinessForm) => void; projectNames: string[]; onSubmit: (event: FormEvent) => void }) {
  const toggleTester = (tester: string) => setForm({ ...form, testers: form.testers.includes(tester) ? form.testers.filter((value) => value !== tester) : [...form.testers, tester] })
  return <form id="readiness-form" className="form-grid two" onSubmit={onSubmit}>
    <Field label="Project"><select value={form.projectName} onChange={(event) => setForm({ ...form, projectName: event.target.value })}>{projectNames.map((name) => <option key={name}>{name}</option>)}</select></Field>
    <Field label="四大類別"><select value={form.groupName} onChange={(event) => setForm({ ...form, groupName: event.target.value })}>{groupOrder.map((name) => <option key={name}>{name}</option>)}{!groupOrder.includes(form.groupName as typeof groupOrder[number]) && form.groupName ? <option>{form.groupName}</option> : null}</select></Field>
    <Field label="Test Item (測項名稱)"><input required value={form.item} onChange={(event) => setForm({ ...form, item: event.target.value })} /></Field>
    <Field label="狀態"><select value={form.status} onChange={(event) => { const status = event.target.value as ReadinessForm['status']; setForm({ ...form, status, statusColor: defaultReadinessStatusColor(status) }) }}>{readinessStatuses.map((value) => <option key={value}>{value}</option>)}</select></Field>
    <Field label="顯示顏色" hint="黃色建議僅用於需要注意的警示">
      <div className="readiness-color-picker" role="radiogroup" aria-label="選擇狀態顏色">
        {readinessStatusColors.map((option) => <button key={option.value} type="button" role="radio" aria-checked={form.statusColor === option.value} className={form.statusColor === option.value ? 'selected' : ''} onClick={() => setForm({ ...form, statusColor: option.value })}><span className={`readiness-color-dot ${option.value}`} />{option.label}</button>)}
      </div>
    </Field>
    <Field label="Trigger / Release (觸發條件)"><input value={form.triggerText} onChange={(event) => setForm({ ...form, triggerText: event.target.value })} /></Field>
    <Field label="測試預估時間 / Duration" hint="例如 約 2–3 週、約 35 分鐘或半天"><input value={form.duration} onChange={(event) => setForm({ ...form, duration: event.target.value })} /></Field>
    <Field label="測試開始時間"><input value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} /></Field>
    <Field label="測試結束時間"><input value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} /></Field>
    <Field label="測試人員" hint="可直接輸入多人，以逗號分隔">
      <div className="owner-selector" role="group" aria-label="選擇 Tester">{workingItemOwners.map((owner) => { const selected = form.testers.includes(owner); return <button key={owner} type="button" className={selected ? 'selected' : ''} aria-pressed={selected} onClick={() => toggleTester(owner)}>{selected ? '✓' : '+'} {owner}</button> })}</div>
      <input value={form.testers.join(', ')} onChange={(event) => setForm({ ...form, testers: event.target.value.split(',').map((value) => value.trim()).filter(Boolean) })} placeholder="直接輸入或點選上方快速選取" />
    </Field>
  </form>
}

function projectDisplayName(project: Project) { return `${project.groupName ? `(${project.groupName}) ` : ''}${project.name}` }
function stageLabel(stage: number) { return projectStageLabels[Math.min(Math.max(stage, 0), projectStageLabels.length - 1)] }
function groupDuration(rows: ReadinessItem[], fallback: string) { const requiredRows = rows.filter((row) => row.status !== '不需測試'); if (rows.length && !requiredRows.length) return '不需測試'; const values = [...new Set(requiredRows.map((row) => row.duration).filter(Boolean))]; return !values.length ? fallback : values.length === 1 ? values[0] : '依測項設定' }
function timeRange(row: ReadinessItem) { return row.startDate && row.endDate ? `${row.startDate} → ${row.endDate}` : row.startDate || row.endDate || '待排定' }
function toReadinessForm(row: ReadinessItem): ReadinessForm { const { projectName, groupName, item, triggerText, status, statusColor, duration, startDate, endDate, testers } = row; return { projectName, groupName, item, triggerText, status, statusColor, duration, startDate, endDate, testers } }
function toProjectForm(row: Project): ProjectForm { const { type, name, groupName, focus, stage, action, description, status, statusColor, targetDate, dateNote } = row; return { type, name, groupName, focus, stage, action, description, status, statusColor, targetDate, dateNote, subtasks: row.subtasks ?? [], links: row.links ?? [], notes: row.notes ?? '' } }
function groupProgressLabel(rows: ReadinessItem[]) { const excluded = rows.filter((row) => row.status === '不需測試').length; const required = rows.length - excluded; const completed = rows.filter((row) => row.status === '已完成').length; return `已完成 ${completed} / ${required}${excluded ? ` · 不需測試 ${excluded}` : ''}` }
